import React, {FC, useEffect, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {Stack, Typography, useTheme} from '@mui/material'
import FormatUtils from '../../../../utils/FormatUtils'
import DemandeAccordionForm from './DemandeAccordionForm'
import demandeUtils from '../../../../utils/demandeUtils'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {styled} from '@mui/material/styles'
import MuiAccordion, {AccordionProps} from '@mui/material/Accordion'
import MuiAccordionSummary, {AccordionSummaryProps} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import {Warning} from '@mui/icons-material'
import {DemandeBean, SuivieDemandeLocationState} from './demandeConstants'
import Loader from '../../../../components/Loader'

type HistoriqueDemandesProps = {
	demandes: any,
	assurePrincipal: any,
	loading: boolean
}

const Accordion = styled(React.forwardRef<HTMLDivElement, AccordionProps>((props, ref) => (
	<MuiAccordion ref={ref} disableGutters elevation={0} square {...props}>
		{props.children}
	</MuiAccordion>
)))(({theme}) => ({
	border: `1px solid ${theme.palette.divider}`,
	'&:before': {
		display: 'none'
	},
	borderRadius: '4px',
	boxShadow: `0px 2px 6px ${theme.palette.divider}`
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
	<MuiAccordionSummary
		expandIcon={<ExpandMoreIcon/>}
		{...props}
	/>
))(({theme}) => ({
	flexDirection: 'row-reverse',
	'& .MuiAccordionSummary-content': {
		marginLeft: theme.spacing(1)
	}
}))

const AccordionDetails = styled(MuiAccordionDetails)(({theme}) => ({
	padding: theme.spacing(2),
	borderTop: '1px solid rgba(0, 0, 0, .125)'
}))

const MARGE_ANCRAGE = 16

/**
 * Remonte jusqu'au premier ancêtre réellement scrollable, sinon la fenêtre.
 */
const conteneurScrollable = (element: HTMLElement): HTMLElement | null => {
	let parent = element.parentElement
	while (parent) {
		const {overflowY} = window.getComputedStyle(parent)
		if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
			return parent
		}
		parent = parent.parentElement
	}
	return null
}

const StackAlert = styled(Stack)(({theme}) => ({
	backgroundColor: '#FAE2D0',
	borderRadius: '4px',
	border: `1px solid ${theme.palette.secondary.main}`
}))

const HistoriqueDemandes: FC<HistoriqueDemandesProps> = (
	{
		demandes,
		assurePrincipal,
		loading
	}
) => {
	const theme = useTheme()
	const location = useLocation<SuivieDemandeLocationState | undefined>()
	const [expanded, setExpanded] = React.useState<string | false>(false)
	const accordeonsRef = useRef<Record<string, HTMLDivElement | null>>({})

	/**
	 * Ancre la demande en haut de la zone de scroll. Le scroll est lancé en même temps que
	 * les animations d'ouverture / fermeture des accordéons : on retire donc de la distance
	 * à parcourir la hauteur du panneau précédent qui, s'il est au-dessus, va se replier et
	 * remonter la cible d'autant. Un seul mouvement continu au lieu de deux enchaînés.
	 */
	const ancrerDemande = (id: number, panelPrecedent: string | false) => {
		const cible = accordeonsRef.current[id]
		if (!cible) {
			return
		}
		const conteneur = conteneurScrollable(cible)
		const hautCible = cible.getBoundingClientRect().top
		let distance = hautCible - (conteneur ? conteneur.getBoundingClientRect().top : 0) - MARGE_ANCRAGE

		const precedent = panelPrecedent ? accordeonsRef.current[panelPrecedent.replace('panel', '')] : null
		if (precedent && precedent.getBoundingClientRect().top < hautCible) {
			distance -= precedent.querySelector('.MuiCollapse-root')?.clientHeight ?? 0
		}

		if (conteneur) {
			conteneur.scrollBy({top: distance, behavior: 'smooth'})
		} else {
			window.scrollBy({top: distance, behavior: 'smooth'})
		}
	}

	const handleChange =
		(panel: string, id: number) => (_: React.SyntheticEvent, newExpanded: boolean) => {
			const panelPrecedent = expanded
			setExpanded(newExpanded ? panel : false)
			if (newExpanded) {
				ancrerDemande(id, panelPrecedent)
			}
		}
	const nbEnAttente = demandeUtils.getDemandesEnAttente(demandes).length

	const dejaScrolleRef = useRef(false)
	const scrollDemandeEnAttente = Boolean(location.state?.scrollToDemandeEnAttente)
	const idDemandeCible = scrollDemandeEnAttente ? demandeUtils.getDemandeLaPlusAncienneEnAttente(demandes)?.id : undefined

	const rafraichissementDemarreRef = useRef(false)
	useEffect(() => {
		if (loading) {
			rafraichissementDemarreRef.current = true
		}
	}, [loading])

	useEffect(() => {
		if (dejaScrolleRef.current || !scrollDemandeEnAttente || loading
			|| !rafraichissementDemarreRef.current || idDemandeCible === undefined
			|| !accordeonsRef.current[idDemandeCible]) {
			return
		}
		dejaScrolleRef.current = true
		ancrerDemande(idDemandeCible, false)
		setExpanded('panel' + idDemandeCible)
	}, [scrollDemandeEnAttente, loading, idDemandeCible])

	if (loading) {
		return (<Stack direction="column" spacing="24px">
				<Loader/>
			</Stack>
		)
	}
	return (!demandes || demandes.length === 0) ? (
		<Typography alignSelf="center" flex={10} variant="body2">
			<FormattedMessage id="suivie_Demande.emptyState"/>
		</Typography>
	) : (
		<Stack direction="column" spacing="16px">
			{
				nbEnAttente > 0 &&
				<StackAlert direction="row" spacing="16px" display="flex" alignItems="center" padding="16px">
					<Warning sx={{color: theme.palette.secondary.main, width: '24px', height: '24px'}}/>
					<Typography variant="body2" style={{wordBreak: 'break-word'}}>
						<FormattedMessage
							id="suivie_Demande.action.libelle"
							values={{
								bold: <b><FormattedMessage id="suivie_Demande.action.actionRequise"/></b>,
								nbDemandes: nbEnAttente
							}}
						/>
					</Typography>
				</StackAlert>
			}
			{demandes.map((demande: DemandeBean) => {
				const canOpen = (demande.documents && demande.documents.length > 0) || (demande.documentsEnAttente && demande.documentsEnAttente.length > 0) || demande.contenuMail
				const index = demande.id
				return (
					<Accordion
						key={index}
						ref={(node: HTMLDivElement | null) => {
							accordeonsRef.current[index] = node
						}}
						expanded={expanded === 'panel' + index}
						sx={{border: (expanded === 'panel' + index) ? `1px solid ${theme.palette.secondary.main}` : null}}
						onChange={canOpen ? handleChange('panel' + index, index) : undefined}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon
								sx={{
									color: canOpen ? '#000000' : '#cccccc',
									fontSize: '40px',
									minWidth: '36px'
								}}/>}
							aria-controls={'panel' + index + 'd-content'}
							id={'panel' + index + 'd-header'}
						>
							<Stack flex={10} direction="row" sx={{padding: '8px'}} display="flex" alignItems="center">
								<Stack flex={6} direction="column">
									<Typography variant="h4">
										{demande.libelle}
									</Typography>
									{demande.beneficiaire && <Typography variant="subtitle1">
										<FormattedMessage id="suivie_Demande.list_demandes.pour"
														  values={{
															  assure: demande.beneficiaire,
															  date: FormatUtils.formatDate(demande.dateDemande)
														  }}
										/>
									</Typography>}
								</Stack>
								<Stack flex={4} direction="row" alignContent="flex-end"
									   justifyContent="flex-end">
									{demandeUtils.demandStatus(demande.etat, {
										variant: 'body2',
										fontWeight: 600
									})}
								</Stack>
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							{canOpen && (
								<Stack direction="column" spacing={2} alignItems="start">
									<DemandeAccordionForm
										demande={demande}
										assurePrincipal={assurePrincipal}
									/>
								</Stack>
							)}
						</AccordionDetails>
					</Accordion>
				)
			})}
		</Stack>
	)
}

export default HistoriqueDemandes
